import * as THREE from "three";

/**
 * Object pool for THREE.Vector2 to prevent garbage collection during drag operations
 * This ensures smooth 60fps performance by reusing objects instead of creating new ones
 */
class Vector2Pool {
  private static instance: Vector2Pool;
  private pool: THREE.Vector2[] = [];
  private maxPoolSize = 50;

  static getInstance(): Vector2Pool {
    if (!Vector2Pool.instance) {
      Vector2Pool.instance = new Vector2Pool();
    }
    return Vector2Pool.instance;
  }

  /**
   * Get a Vector2 from the pool or create a new one if pool is empty
   */
  get(x: number = 0, y: number = 0): THREE.Vector2 {
    let vector = this.pool.pop();
    if (!vector) {
      vector = new THREE.Vector2();
    }
    vector.set(x, y);
    return vector;
  }

  /**
   * Return a Vector2 to the pool for reuse
   */
  release(vector: THREE.Vector2): void {
    if (this.pool.length < this.maxPoolSize) {
      vector.set(0, 0); // Reset to avoid memory leaks
      this.pool.push(vector);
    }
  }

  /**
   * Get a temporary Vector2 that will be automatically released
   * Use this for short-lived operations during drag
   */
  getTemporary(x: number = 0, y: number = 0): { vector: THREE.Vector2; release: () => void } {
    const vector = this.get(x, y);
    return {
      vector,
      release: () => this.release(vector)
    };
  }

  /**
   * Clear the pool (call this when drag operations end)
   */
  clear(): void {
    this.pool.length = 0;
  }
}

export default Vector2Pool.getInstance();
